<script setup lang="ts">
// DUPLICATED/adapted from packages/frontend/src/modules/browser/components/rules/RuleComponent.vue
// for the studio preview. This is a DISPLAY-ONLY SUBSET: ContentSection
// collapse/expand, router links, and search highlighting were dropped. The accordion
// contentType is rendered as a plain title + body instead. Keep the content-type
// dispatch and text rendering in sync with the frontend original; do not re-add
// app-specific chrome here.
//
// Import rewiring vs original:
//   - formatText → ../../utils/formatter.js (local copy, no search highlighting)
//   - AbilityCard → ../AbilityCard.vue (local editor copy)
//   - ContentSection → removed (accordion rendered as plain heading + body)
//   - fromAoSData → @scrolldex/shared
import { computed } from 'vue';
import { fromAoSData, type RuleComponent } from '@scrolldex/shared';
import AbilityCard from '../AbilityCard.vue';
import { formatText } from '../../utils/formatter.js';

const props = defineProps<{
  component: RuleComponent;
}>();

const abilityObj = computed(() => {
  if (props.component.contentType === 'ability' && props.component.ability) {
    return fromAoSData(props.component.ability);
  }
  return null;
});
</script>
<template>
  <div
    v-if="['text', 'scoring', 'twist', 'boxed'].includes(component.contentType)"
    :class="component.contentType"
    v-html="formatText(component.text ?? '')"
  />
  <div
    v-else-if="component.contentType === 'header'"
    class="rule-header"
    v-html="formatText(component.text ?? '')"
  />
  <div v-else-if="component.contentType === 'accordion'" class="rule-accordion">
    <div class="rule-accordion__title">{{ component.title }}</div>
    <div v-html="formatText(component.text ?? '')" />
  </div>
  <AbilityCard
    v-else-if="component.contentType === 'ability' && abilityObj"
    :ability="abilityObj"
  />
</template>
<style scoped>
/* Studio-elevation preview styling (deliberately drifts from the app theme so the
   document reads on the studio's light surface). Plain text flows; callouts and
   notes raise/recede via the --st-* elevation tokens. */
.text {
  margin: var(--st-space-2) 0;
  color: var(--st-text);
  line-height: 1.55;
  text-align: left;
}

.rule-header {
  font-size: var(--st-text-md);
  font-weight: var(--st-weight-semibold);
  color: var(--st-text);
  margin: var(--st-space-3) 0 var(--st-space-1);
}

/* Boxed note — a recessed panel. */
.boxed {
  margin: var(--st-space-2) 0;
  padding: var(--st-space-3);
  color: var(--st-text);
  line-height: 1.55;
  background: var(--st-surface-sunken);
  border: 1px solid var(--st-border);
  border-radius: var(--st-radius-md);
}

/* Twist — an accent callout. */
.twist {
  margin: var(--st-space-2) 0;
  padding: var(--st-space-3);
  color: var(--st-text);
  line-height: 1.55;
  background: var(--st-accent-tint);
  border-left: 4px solid var(--st-accent);
  border-radius: var(--st-radius-sm);
}

/* Scoring — a neutral raised callout, distinct from the twist. */
.scoring {
  margin: var(--st-space-2) 0;
  padding: var(--st-space-3);
  color: var(--st-text);
  line-height: 1.55;
  background: var(--st-surface);
  border: 1px solid var(--st-border);
  border-left: 4px solid var(--st-border-strong);
  border-radius: var(--st-radius-sm);
  box-shadow: var(--st-shadow-1);
}

.rule-accordion {
  margin: var(--st-space-2) 0;
  padding: var(--st-space-3);
  background: var(--st-surface-sunken);
  border: 1px solid var(--st-border);
  border-radius: var(--st-radius-md);
}

.rule-accordion__title {
  font-weight: var(--st-weight-semibold);
  font-size: var(--st-text-md);
  color: var(--st-text);
  margin-bottom: var(--st-space-1);
}
</style>
